(() => {
  if (window.AFRWishlist) return;
  const key = "afr:wishlist:v1";
  const valid = item => item && /^\d+$/.test(String(item.id)) && typeof item.handle === "string" && item.handle.length > 0 && item.handle.length < 256 && !/[\/\\?#\u0000-\u001f]/.test(item.handle);
  const normalize = value => {
    if (!Array.isArray(value)) return [];
    const seen = new Set();
    return value.filter(item => {
      if (!valid(item) || seen.has(String(item.id))) return false;
      seen.add(String(item.id));
      return true;
    }).slice(0, 100).map(item => ({ id: String(item.id), handle: item.handle }));
  };
  const read = () => {
    try { return normalize(JSON.parse(localStorage.getItem(key) || "[]")); }
    catch { return []; }
  };
  let items = read();
  let renderController;
  const saved = id => items.some(item => item.id === String(id));
  const updateButtons = root => {
    const buttons = [];
    if (root.matches?.("[data-afr-wishlist-toggle]")) buttons.push(root);
    buttons.push(...root.querySelectorAll("[data-afr-wishlist-toggle]"));
    buttons.forEach(button => {
      const selected = saved(button.dataset.productId);
      button.setAttribute("aria-pressed", String(selected));
      button.setAttribute("aria-label", selected ? button.dataset.labelRemove : button.dataset.labelAdd);
      button.title = selected ? button.dataset.labelRemove : button.dataset.labelAdd;
    });
  };
  const updateCounts = () => document.querySelectorAll("[data-afr-wishlist-count]").forEach(node => {
    const text = String(items.length);
    if (node.textContent !== text) node.textContent = text;
    node.hidden = items.length === 0;
  });
  const announce = message => {
    let node = document.getElementById("afrWishlistStatus");
    if (!node) {
      node = document.createElement("p");
      node.id = "afrWishlistStatus";
      node.className = "afr-wishlist-status";
      node.setAttribute("role", "status");
      node.setAttribute("aria-live", "polite");
      document.body.append(node);
    }
    node.textContent = message;
  };
  const toggle = item => {
    if (!valid(item)) return { changed: false, persisted: false };
    const id = String(item.id);
    const exists = saved(id);
    if (!exists && items.length >= 100) return { changed: false, persisted: false };
    items = exists ? items.filter(entry => entry.id !== id) : [{ id, handle: item.handle }, ...items];
    let persisted = true;
    try { localStorage.setItem(key, JSON.stringify(items)); } catch { persisted = false; }
    updateButtons(document);
    updateCounts();
    document.dispatchEvent(new CustomEvent("afr:wishlist-change"));
    return { changed: true, selected: !exists, persisted };
  };
  const productUrl = (root, handle) => {
    const base = new URL(root || "/", location.origin);
    if (base.origin !== location.origin) throw new Error("Invalid product root");
    const url = new URL(base.pathname.replace(/\/?$/, "/") + "products/" + encodeURIComponent(handle), location.origin);
    url.searchParams.set("view", "afr-wishlist-card");
    const preview = new URL(location.href).searchParams.get("preview_theme_id");
    if (preview && /^\d+$/.test(preview)) url.searchParams.set("preview_theme_id", preview);
    return url;
  };
  const renderWishlist = async () => {
    const page = document.querySelector("[data-afr-wishlist-page]");
    if (!page) return;
    renderController?.abort();
    const controller = new AbortController();
    renderController = controller;
    const grid = page.querySelector("[data-afr-wishlist-grid]");
    const status = page.querySelector("[data-afr-wishlist-page-status]");
    const selected = items.slice();
    grid.replaceChildren();
    grid.setAttribute("aria-busy", String(selected.length > 0));
    status.textContent = selected.length ? page.dataset.loading : page.dataset.empty;
    if (!selected.length) return;
    const slots = selected.map(() => {
      const slot = document.createElement("div");
      slot.className = "product-card";
      grid.append(slot);
      return slot;
    });
    let cursor = 0;
    let errors = 0;
    const worker = async () => {
      while (cursor < selected.length && !controller.signal.aborted) {
        const index = cursor++;
        const item = selected[index];
        try {
          const url = productUrl(page.dataset.productRoot, item.handle);
          const response = await fetch(url, { signal: controller.signal, credentials: "same-origin" });
          if (!response.ok) throw new Error("Product unavailable");
          const doc = new DOMParser().parseFromString(await response.text(), "text/html");
          const fragment = doc.querySelector("[data-afr-wishlist-card]");
          if (!fragment?.querySelector(".afr-card-with-cart")) throw new Error("Card unavailable");
          if (controller.signal.aborted || !saved(item.id)) continue;
          slots[index].append(...Array.from(fragment.childNodes));
          updateButtons(slots[index]);
          requestAnimationFrame(() => document.dispatchEvent(new CustomEvent("maximize:animation:reveal")));
        } catch (error) {
          if (controller.signal.aborted) return;
          errors++;
          const fallback = document.createElement("div");
          fallback.className = "afr-wishlist-fallback";
          const text = document.createElement("p");
          text.textContent = page.dataset.unavailable;
          const link = document.createElement("a");
          const url = productUrl(page.dataset.productRoot, item.handle);
          url.searchParams.delete("view");
          link.href = url.href;
          link.textContent = item.handle;
          const remove = document.createElement("button");
          remove.type = "button";
          remove.textContent = page.dataset.remove;
          remove.addEventListener("click", () => toggle(item));
          fallback.append(text, link, remove);
          slots[index].append(fallback);
        }
      }
    };
    await Promise.all(Array.from({ length: Math.min(4, selected.length) }, worker));
    if (!controller.signal.aborted) {
      grid.setAttribute("aria-busy", "false");
      status.textContent = errors ? page.dataset.error : "";
    }
  };
  window.AFRWishlist = { getItems: () => items.map(item => ({ ...item })), toggle };
  const init = () => {
    updateButtons(document);
    updateCounts();
    document.addEventListener("click", event => {
      const heart = event.target.closest?.("[data-afr-wishlist-toggle]");
      if (heart) {
        event.preventDefault();
        const result = toggle({ id: heart.dataset.productId, handle: heart.dataset.productHandle });
        if (result.changed) announce(result.persisted ? (result.selected ? heart.dataset.messageAdded : heart.dataset.messageRemoved) : heart.dataset.storageError);
        return;
      }
      const eye = event.target.closest?.("[data-afr-quick-view]");
      if (eye) {
        event.preventDefault();
        const store = window.Alpine?.store("xQuickView");
        if (store && store.enabled && store.load(eye.dataset.productId, eye.dataset.productUrl, eye)) store.open();
        else location.assign(eye.dataset.productUrl);
      }
    });
    document.addEventListener("afr:wishlist-change", renderWishlist);
    window.addEventListener("storage", event => {
      if (event.key !== key && event.key !== null) return;
      items = read();
      updateButtons(document);
      updateCounts();
      renderWishlist();
    });
    new MutationObserver(records => {
      const roots = new Set();
      records.forEach(record => record.addedNodes.forEach(node => {
        if (node.nodeType === 1 && (node.matches?.("[data-afr-wishlist-toggle]") || node.querySelector?.("[data-afr-wishlist-toggle]"))) roots.add(node);
      }));
      if (!roots.size) return;
      roots.forEach(updateButtons);
      updateCounts();
    }).observe(document.body, { childList: true, subtree: true });
    window.addEventListener("pagehide", () => renderController?.abort());
    renderWishlist();
  };
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init, { once: true });
  else init();
})();